---
title: Authentication
description: Store API keys, allowed origins and scopes, shopper sessions, and rate limits.
order: 2
---

Every request carries a store API key as a bearer token. Requests made for a signed-in shopper
carry a second credential, the shopper's access token, in its own header. The examples reuse
`NUCLEUS_API_URL`, `NUCLEUS_KEY` and the SDK client `storefront` from
[Getting started](/docs).

## Publishable and secret keys

|                    | Publishable key                    | Secret key                                          |
| ------------------ | ---------------------------------- | --------------------------------------------------- |
| Prefix             | `pk_live_` or `pk_test_`           | `sk_live_` or `sk_test_`                            |
| Where it runs      | Browser or server                  | Server only                                         |
| Browser origins    | Only the origins listed on the key | Refused whenever the request has an `Origin` header |
| Default rate limit | 600 requests a minute              | 2,000 requests a minute                             |

Both are sent the same way:

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/store" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

In the SDK, pass the key as `publishableKey` or `secretKey` to `createStorefrontClient`, never
both.

### Allowed origins

A browser adds an `Origin` header to cross-origin requests. The API compares it with the key's
allowed origins exactly: scheme, host and port, so `https://shop.example.com` and
`https://www.shop.example.com` are two entries. A browser request from an origin the key does not
list fails with `403 ORIGIN_NOT_ALLOWED`.

Allowed origins stop another website from running your key in its visitors' browsers. They do not
make the key a secret: code that runs outside a browser sends no `Origin`, and a publishable key
works from there too. That is why a publishable key only reaches what a shopper could see anyway.

The same list guards the URLs the API puts into emails and redirects: the `redirectUrl` of a magic
link or Google sign-in and the `resetUrl` of a password reset must be on one of the key's origins,
otherwise the request fails with `403 ORIGIN_NOT_ALLOWED` on that field.

### Secret keys stay on the server

A secret key sent with an `Origin` header fails with `403 SECRET_KEY_IN_BROWSER`, even from an
origin you trust. Use it from your storefront's server, for example to render pages or to read
draft sections with `?preview=true` on <EndpointLink id="getSection" />.

## Scopes

A key can be limited to parts of the API. A key with no scopes may call everything; a key with
scopes answers `403 PERMISSION_DENIED` outside them, with the missing scope in `meta.scope`.

| Scope             | Endpoints                                                                                           |
| ----------------- | --------------------------------------------------------------------------------------------------- |
| `catalog:read`    | `/store`, `/products`, `/collections`, `/search`, reading `/auctions`                               |
| `content:read`    | `/pages`, `/blog`, `/menus`, `/blocks`, `/sections`, `/redirects`, sitemaps, `robots.txt`, IndexNow |
| `cart:write`      | `/carts`                                                                                            |
| `checkout:write`  | `/checkouts`                                                                                        |
| `customers:write` | `/customers`, `/wishlists`                                                                          |
| `orders:read`     | `/orders`                                                                                           |
| `analytics:write` | `/events`                                                                                           |
| `chat:write`      | `/chat`                                                                                             |
| `auctions:write`  | registering for and bidding in `/auctions`                                                          |

```json title="403 from a catalog:read key calling POST /carts"
{
  "error": {
    "code": "PERMISSION_DENIED",
    "message": "This API key does not have the cart:write scope",
    "requestId": "01a0f3a6-be01-746c-b446-8ac9596d3c6a",
    "meta": { "scope": "cart:write" }
  }
}
```

## Key errors

| Status | Code                    | Meaning                                                          |
| ------ | ----------------------- | ---------------------------------------------------------------- |
| 401    | `UNAUTHORIZED`          | No `Authorization` header                                        |
| 401    | `INVALID_API_KEY`       | The key is unknown or was revoked                                |
| 403    | `ORIGIN_NOT_ALLOWED`    | Browser request from an origin the publishable key does not list |
| 403    | `SECRET_KEY_IN_BROWSER` | A secret key sent with an `Origin` header                        |
| 403    | `PERMISSION_DENIED`     | The key's scopes do not cover this endpoint (`meta.scope`)       |
| 403    | `STORE_CLOSED`          | The store behind the key is closed                               |

## Shopper sessions

Shopper accounts sign in with <EndpointLink id="loginCustomer" />,

<EndpointLink id="registerCustomer" />, a magic link or Google (see [Customer
accounts](/docs/guides/customer-accounts)). Each answers with the same token pair:

| Field          | Lifetime   | Use                                                                       |
| -------------- | ---------- | ------------------------------------------------------------------------- |
| `accessToken`  | 15 minutes | Sent as `X-Customer-Token` next to the store key (`expiresIn` in seconds) |
| `refreshToken` | 30 days    | Exchanged for a new pair; works once (`refreshExpiresAt`)                 |

Endpoints that act for a shopper, such as <EndpointLink id="getCustomerProfile" />, are marked
**Customer token** on their reference page and answer `401 UNAUTHORIZED` without one. An access
token only works with keys of the store that issued it.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/login" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "email": "asha@example.com", "password": "correct-horse-battery-9" }'

# Then, with the accessToken from the response:
curl "$NUCLEUS_API_URL/storefront/v1/customers/me" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN"
```

```ts title="fetch"
const session = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/login`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ email: 'asha@example.com', password: 'correct-horse-battery-9' }),
}).then((response) => response.json());

const profile = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'X-Customer-Token': session.accessToken },
}).then((response) => response.json());
```

```ts title="SDK"
const session = await storefront.loginCustomer({
  email: 'asha@example.com',
  password: 'correct-horse-battery-9',
});

// A copy of the client that sends X-Customer-Token; `storefront` itself is unchanged.
const shopper = storefront.withCustomerToken(session.accessToken);
const profile = await shopper.getCustomerProfile();
```

</CodeTabs>

### Refreshing the session

When a request fails with `401 TOKEN_EXPIRED`, exchange the refresh token for a new pair with

<EndpointLink id="refreshCustomerSession" /> and retry. Every refresh token works **once**. Sending
one that was already used fails with `401 TOKEN_INVALID` and revokes every refresh token of that
session, on the assumption that it was stolen; access tokens already issued keep working until they
expire. So store the new pair before you use it, and when two tabs refresh at the same time, let one
of them do it.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/refresh" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{ \"refreshToken\": \"$REFRESH_TOKEN\" }"
```

```ts title="fetch"
const refreshed = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/refresh`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ refreshToken: session.refreshToken }),
}).then((response) => response.json());
```

```ts title="SDK"
const refreshed = await storefront.refreshCustomerSession({
  refreshToken: session.refreshToken,
});
const signedIn = storefront.withCustomerToken(refreshed.accessToken);
```

</CodeTabs>

`401 TOKEN_INVALID` on any other request means the access token is malformed or belongs to
another store: sign the shopper out.

### Signing out

<EndpointLink id="logoutCustomer" /> revokes the refresh token and answers `204`. Drop both tokens
on your side too; the access token stays valid until it expires.

## Rate limits

Limits count requests per key per minute: 600 for a publishable key and 2,000 for a secret key
unless the key has its own limit. A few shopper endpoints also count per IP address, so one visitor
cannot hammer them through your storefront's key:

| Endpoints                                                                                | Per IP       |
| ---------------------------------------------------------------------------------------- | ------------ |
| Register, login, magic link verify, Google start and complete, password reset and change | 10 a minute  |
| <EndpointLink id="forgotPassword" />, <EndpointLink id="requestMagicLink" />             | 5 a minute   |
| <EndpointLink id="refreshCustomerSession" />                                             | 60 a minute  |
| <EndpointLink id="trackEvents" />                                                        | 120 a minute |
| <EndpointLink id="createPrivacyRequest" />                                               | 10 an hour   |

Every response carries the current window, readable from browser code too:

| Header                  | Meaning                              |
| ----------------------- | ------------------------------------ |
| `x-ratelimit-limit`     | Requests allowed in the window       |
| `x-ratelimit-remaining` | Requests left in the window          |
| `x-ratelimit-reset`     | Seconds until the window starts over |

Over the limit, the API answers `429 RATE_LIMITED` with a `Retry-After` header and the same number
of seconds in `meta.retryAfterSeconds`. Wait that long before retrying.
